<template>
  <div class="verifikasi-detail-page">
    <!-- Loading State -->
    <div v-if="pageLoading" class="loading-container">
      <a-spin size="large" tip="Memuat data surat..." />
    </div>

    <template v-else>
      <!-- Page Header -->
      <div class="page-header">
        <div class="header-title-wrapper">
          <h1>Detail Surat Keluar</h1>
          <div class="status-surat">
            <a-tag :color="getStatusColor(verifikasiStatus)">
              <strong>{{ getStatusText(verifikasiStatus) }}</strong>
            </a-tag>
          </div>
        </div>
      </div>

      <!-- Main Content -->
      <div class="content-container">
        <h4 class="section-title">Informasi Surat</h4>

        <!-- Info Fields using a-row like UpdateSuratMasuk -->
        <div class="info-fields">
          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Pengaju Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">
                {{ suratData?.creator?.name || '-' }} ({{
                  suratData?.creator?.jabatan || suratData?.creator?.role || 'Staf Sekretariat'
                }})
              </span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Waktu Dibuat/Unggah</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ formatDateTime(suratData?.createdAt) }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Jenis Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData?.jenisSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Nomor Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData?.nomorSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Sifat Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData?.sifatSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Bentuk Surat</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData?.bentukSurat || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row" v-if="suratData?.metodePembuatan">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Metode Pembuatan</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ suratData?.metodePembuatan || '-' }}</span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Penandatangan 1</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value"
                >{{ suratData?.penandatanganKiri?.name || '-' }} ({{
                  suratData?.penandatanganKiri?.jabatan || ''
                }})</span
              >
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Penandatangan 2</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value"
                >{{
                  suratData?.penandatanganKanan?.name
                    ? suratData?.penandatanganKanan?.name +
                      ' (' +
                      suratData?.penandatanganKanan?.jabatan +
                      ')'
                    : 'Tidak ada'
                }}
              </span>
            </a-col>
          </a-row>

          <a-row :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Verifikator</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">{{ formatVerifikatorList(suratData?.verifikator) }}</span>
            </a-col>
          </a-row>

          <a-row v-if="suratData?.suratUndanganAcara" :gutter="16" class="info-row">
            <a-col :xs="24" :sm="8" :md="6">
              <span class="info-label">Surat Undangan Acara</span>
            </a-col>
            <a-col :xs="24" :sm="16" :md="18">
              <span class="info-value">
                <a @click="openSuratMasukPreview" target="_blank" class="file-link">
                  <PaperClipOutlined />
                  {{
                    suratData.suratUndanganAcara.perihal || suratData.suratUndanganAcara.nomorSurat
                  }}
                </a>
                <a-button type="text" class="btn-preview-small" @click="openSuratMasukPreview">
                  <EyeOutlined />
                </a-button>
                <a-button type="text" class="btn-external-small" @click="openSuratMasukPreview">
                  <ExportOutlined />
                </a-button>
              </span>
            </a-col>
          </a-row>
        </div>

        <!-- Detail Revisi Section (only show verifikator revisions) -->
        <DetailRevisi
          :verifikator="suratData?.verifikator"
          :penandatangan="suratData?.penandatangan"
          show-only="verifikator"
        />

        <!-- Dokumen Surat Section -->
        <div class="dokumen-section">
          <h4 class="section-title">{{ isSuratTugas ? 'Surat Tugas' : 'Dokumen Surat' }}</h4>

          <!-- Upload File Method: Show uploaded files with preview -->
          <template v-if="isUploadFileMethod && uploadedFileList.length > 0">
            <div class="uploaded-files-container">
              <div
                v-for="(file, index) in uploadedFileList"
                :key="index"
                class="file-item"
              >
                <PaperClipOutlined class="file-icon" />
                <a :href="file.url" target="_blank" class="file-link">
                  {{ file.name }}
                </a>
                <div class="file-actions">
                  <a-button type="text" size="small" @click="openUploadedFilePreview(file)">
                    <EyeOutlined />
                  </a-button>
                  <a-button type="text" size="small" @click="() => window.open(file.url, '_blank')">
                    <ExportOutlined />
                  </a-button>
                </div>
              </div>
            </div>

            <!-- Preview Section for Uploaded Files -->
            <div v-if="uploadedFilePreviewVisible" class="uploaded-preview-section">
              <div class="preview-header">
                <span class="preview-title">{{ uploadedFilePreviewTitle }}</span>
                <a-button type="text" size="small" @click="closeUploadedFilePreview">
                  <CloseOutlined />
                </a-button>
              </div>
              <div class="preview-content">
                <iframe
                  v-if="isPdfFile(uploadedFilePreviewPath)"
                  :src="uploadedFilePreviewPath"
                  class="preview-iframe"
                />
                <img
                  v-else
                  :src="uploadedFilePreviewPath"
                  class="preview-image"
                />
              </div>
              <div class="preview-actions">
                <a-button type="primary" @click="() => window.open(uploadedFilePreviewPath, '_blank')">
                  <ExportOutlined /> Buka di Tab Baru
                </a-button>
              </div>
            </div>
          </template>

          <!-- Buat di Sistem Method: Show generated PDF or Surat Tugas -->
          <SuratTugasViewer
            v-else
            :surat-tugas-list="suratTugasList"
            :single-pdf-path="
              !isSuratTugas || suratTugasList.length === 0 ? suratData?.pdfPath : ''
            "
            :nomor-surat="suratData?.nomorSurat"
            :empty-message="
              isSuratTugas ? 'Belum ada surat tugas yang dibuat' : 'PDF surat belum tersedia'
            "
          />
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons" v-if="canVerify">
          <a-button
            type="primary"
            class="btn-approve"
            @click="showApproveModal"
            :loading="submitting"
          >
            SETUJUI SURAT
          </a-button>
          <a-button
            type="default"
            class="btn-revise"
            @click="showReviseModal"
            :loading="submitting"
          >
            MINTA REVISI
          </a-button>
        </div>
      </div>
    </template>

    <!-- Approve Modal -->
    <a-modal
      v-model:visible="approveModalVisible"
      :footer="null"
      :closable="true"
      width="500px"
      centered
    >
      <div class="modal-confirm">
        <h3 class="modal-title">Setujui surat keluar?</h3>
        <p class="modal-description">
          Mohon cek kembali data surat sebelum menyimpan formulir. Surat yang sudah diverifikasi
          akan dikirimkan ke verifikator selanjutnya atau penandatangan untuk diproses.
        </p>
        <div class="modal-actions">
          <a-button class="btn-cancel" @click="approveModalVisible = false" :disabled="submitting">
            KEMBALI
          </a-button>
          <a-button class="btn-confirm" @click="handleApprove" :loading="submitting">
            YA, SIMPAN
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- Revise Modal -->
    <a-modal
      v-model:visible="reviseModalVisible"
      :footer="null"
      :closable="true"
      width="600px"
      centered
    >
      <div class="modal-confirm">
        <h3 class="modal-title">Minta sekretaris merevisi surat?</h3>
        <p class="modal-description">
          Surat akan dikembalikan ke pembuat surat untuk direvisi kembali. Mohon masukkan catatan
          hal yang perlu direvisi pada surat.
        </p>
        <a-form layout="vertical">
          <a-form-item label="Catatan Revisi" required class="form-item-revisi">
            <a-textarea
              v-model:value="reviseNotes"
              placeholder="Masukkan catatan revisi yang perlu diperbaiki pengaju surat"
              :rows="4"
            />
          </a-form-item>
        </a-form>
        <div class="modal-actions">
          <a-button class="btn-cancel" @click="handleCancelRevise" :disabled="submitting">
            KEMBALI
          </a-button>
          <a-button class="btn-confirm" @click="handleRevise" :loading="submitting">
            YA, SIMPAN
          </a-button>
        </div>
      </div>
    </a-modal>

    <!-- PDF Preview Modal for Surat Masuk -->
    <PDFPreviewModal
      v-model:visible="pdfPreviewVisible"
      :pdf-path="pdfPreviewPath"
      :title="pdfPreviewTitle"
      :show-download="true"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { notification } from 'ant-design-vue'
import { PaperClipOutlined, EyeOutlined, ExportOutlined, CloseOutlined } from '@ant-design/icons-vue'
import moment from 'moment'
import { suratKeluarAPI, verifikasiAPI, getFileUrl } from '@/services/axios/perki'
import { DetailRevisi, SuratTugasViewer, PDFPreviewModal } from '@/components/SuratKeluar'

export default {
  name: 'VerifikasiDetail',
  components: {
    PaperClipOutlined,
    EyeOutlined,
    ExportOutlined,
    CloseOutlined,
    DetailRevisi,
    SuratTugasViewer,
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const pageLoading = ref(true)
    const submitting = ref(false)
    const suratData = ref({})
    const approveModalVisible = ref(false)
    const reviseModalVisible = ref(false)
    const reviseNotes = ref('')

    // PDF Preview modal (for Surat Masuk preview)
    const pdfPreviewVisible = ref(false)
    const pdfPreviewPath = ref('')
    const pdfPreviewTitle = ref('')

    // Uploaded file preview state
    const uploadedFilePreviewVisible = ref(false)
    const uploadedFilePreviewPath = ref('')
    const uploadedFilePreviewTitle = ref('')

    const currentUserId = localStorage.getItem('perki_user')
      ? JSON.parse(localStorage.getItem('perki_user')).id
      : null

    // Check if this is Surat Tugas type
    const isSuratTugas = computed(() => {
      return suratData.value?.jenisSurat === 'Surat Tugas'
    })

    // Build surat tugas list from perwakilan dinas
    const suratTugasList = computed(() => {
      if (!suratData.value?.perwakilanDinas) return []
      return suratData.value.perwakilanDinas
        .filter(p => p.pdfPath)
        .map(p => ({
          id: p.id,
          pdfPath: p.pdfPath,
          perwakilan: p,
          fileName: `Surat Tugas ${suratData.value.nomorSurat}`,
        }))
    })

    // Check if surat uses "Upload File" method
    const isUploadFileMethod = computed(() => {
      return suratData.value?.metodePembuatan === 'Upload File'
    })

    // Get uploaded file paths from archivedPdfPaths
    const uploadedFileList = computed(() => {
      if (!isUploadFileMethod.value || !suratData.value?.archivedPdfPaths) {
        return []
      }
      try {
        const paths = JSON.parse(suratData.value.archivedPdfPaths)
        if (Array.isArray(paths) && paths.length > 0) {
          return paths.map((path, index) => ({
            path: path,
            name: getFileNameFromPath(path) || `Dokumen ${index + 1}`,
            url: getFileUrl(path),
          }))
        }
      } catch (e) {
        console.error('Failed to parse archivedPdfPaths:', e)
      }
      return []
    })

    // Helper to extract filename from path
    const getFileNameFromPath = (path) => {
      if (!path) return ''
      const parts = path.split('/')
      return parts[parts.length - 1]
    }

    // Get current verifikator status for logged-in user
    const currentVerifikator = computed(() => {
      if (!suratData.value?.verifikator || !currentUserId) return null
      return suratData.value.verifikator.find(v => v.userId === currentUserId)
    })

    const verifikasiStatus = computed(() => {
      return currentVerifikator.value?.status || 'pending'
    })

    const canVerify = computed(() => {
      if (!currentVerifikator.value) return false
      // Can only verify if status is pending
      if (currentVerifikator.value.status !== 'pending') return false

      // Check if all previous verifikators are approved
      if (!suratData.value?.verifikator) return false

      const sortedVerifikator = [...suratData.value.verifikator].sort((a, b) => {
        const urutanA = a.urutan || 0
        const urutanB = b.urutan || 0
        return urutanA - urutanB
      })

      const currentIndex = sortedVerifikator.findIndex(v => v.userId === currentUserId)
      if (currentIndex === -1) return false

      // Check if all previous verifikators are approved
      const allPreviousApproved = sortedVerifikator
        .slice(0, currentIndex)
        .every(prev => prev.status === 'approved')

      return allPreviousApproved
    })

    const formatDateTime = dateString => {
      if (!dateString) return '-'
      return moment(dateString).format('dddd, DD MMMM YYYY HH:mm')
    }

    const getStatusColor = status => {
      // Surat status takes priority for certain statuses
      if (suratData.value?.status === 'Dibatalkan') {
        return 'default' // Gray for Dibatalkan
      }

      // Dalam Antrian: pending but not current turn
      if (status === 'pending' && !canVerify.value) {
        return 'blue'
      }

      const colors = {
        pending: 'red', // Butuh Verifikasi (current turn)
        approved: 'green',
        rejected: 'default',
        revised: 'orange',
        revision_requested: 'orange',
      }
      return colors[status] || 'default'
    }

    const getStatusText = status => {
      // Surat status takes priority for certain statuses
      if (suratData.value?.status === 'Dibatalkan') {
        return 'Dibatalkan'
      }

      // Check if user is pending but not their turn yet (Dalam Antrian)
      if (status === 'pending' && !canVerify.value) {
        return 'Dalam Antrian'
      }

      const texts = {
        pending: 'Butuh Verifikasi',
        approved: 'Disetujui',
        rejected: 'Ditolak',
        revised: 'Revisi',
        revision_requested: 'Revisi Diajukan',
      }
      return texts[status] || status || 'Pending'
    }

    const formatVerifikatorList = verifikator => {
      if (!verifikator || verifikator.length === 0) return '-'

      // Sort by urutan first (ascending)
      const sortedVerifikator = [...verifikator].sort((a, b) => {
        const urutanA = a.urutan || 0
        const urutanB = b.urutan || 0
        return urutanA - urutanB
      })

      return sortedVerifikator
        .map((v, index) => {
          const name = v.user?.name || 'Unknown'
          let statusText = ''

          if (v.status === 'approved') {
            statusText = '(Setuju)'
          } else if (v.status === 'revised' || v.status === 'revision_requested') {
            statusText = '(Revisi)'
          } else if (v.status === 'pending') {
            // Check if all previous verifikators are approved
            const allPreviousApproved = sortedVerifikator
              .slice(0, index)
              .every(prev => prev.status === 'approved')

            if (allPreviousApproved) {
              statusText = '(Sedang Verifikasi)'
            } else {
              statusText = '(Menunggu)'
            }
          }
          return `${name} ${statusText}`
        })
        .join(' → ')
    }

    const fetchDetail = async () => {
      pageLoading.value = true
      try {
        const id = route.params.id
        const response = await suratKeluarAPI.getById(id)

        if (response.data?.status === 'success' || response.data?.data) {
          suratData.value = response.data.data || response.data
          console.log(suratData.value, 'cekini')
        } else {
          notification.error({
            message: 'Error',
            description: 'Gagal memuat detail surat',
          })
          router.push('/verifikasi')
        }
      } catch (error) {
        console.error('Error fetching detail:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal memuat detail surat',
        })
        router.push('/verifikasi')
      } finally {
        pageLoading.value = false
      }
      console.log(suratData.value, 'cekini')
    }

    const showApproveModal = () => {
      approveModalVisible.value = true
    }

    const showReviseModal = () => {
      reviseModalVisible.value = true
    }

    const handleCancelRevise = () => {
      reviseModalVisible.value = false
      reviseNotes.value = ''
    }

    const handleApprove = async () => {
      if (!currentVerifikator.value) {
        notification.error({
          message: 'Error',
          description: 'Verifikator tidak ditemukan',
        })
        return
      }

      submitting.value = true
      try {
        const response = await verifikasiAPI.approve(route.params.id)

        if (response.data?.status === 'success') {
          notification.success({
            message: 'Berhasil',
            description: 'Surat berhasil disetujui',
          })
          approveModalVisible.value = false
          setTimeout(() => {
            router.push('/verifikasi')
          }, 500)
        } else {
          notification.error({
            message: 'Error',
            description: 'Gagal menyetujui surat',
          })
        }
      } catch (error) {
        console.error('Error approving:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal menyetujui surat',
        })
      } finally {
        submitting.value = false
      }
    }

    const handleRevise = async () => {
      if (!reviseNotes.value.trim()) {
        notification.error({
          message: 'Error',
          description: 'Catatan revisi harus diisi',
        })
        return
      }

      if (!currentVerifikator.value) {
        notification.error({
          message: 'Error',
          description: 'Verifikator tidak ditemukan',
        })
        return
      }

      submitting.value = true
      try {
        const response = await verifikasiAPI.revise(route.params.id, {
          catatanRevisi: reviseNotes.value,
        })

        if (response.data?.status === 'success') {
          notification.success({
            message: 'Berhasil',
            description: 'Permintaan revisi berhasil dikirim',
          })
          reviseModalVisible.value = false
          reviseNotes.value = ''
          setTimeout(() => {
            router.push('/verifikasi')
          }, 500)
        } else {
          notification.error({
            message: 'Error',
            description: 'Gagal mengirim permintaan revisi',
          })
        }
      } catch (error) {
        console.error('Error revising:', error)
        notification.error({
          message: 'Error',
          description: error.response?.data?.message || 'Gagal mengirim permintaan revisi',
        })
      } finally {
        submitting.value = false
      }
    }

    // Open surat masuk preview
    const openSuratMasukPreview = () => {
      if (suratData.value?.suratUndanganAcara?.pathToFile) {
        pdfPreviewPath.value = suratData.value.suratUndanganAcara.pathToFile
        pdfPreviewTitle.value =
          suratData.value.suratUndanganAcara.perihal ||
          suratData.value.suratUndanganAcara.nomorSurat ||
          'Surat Masuk'
        pdfPreviewVisible.value = true
      }
    }

    // Open preview for uploaded files (Upload File method)
    const openUploadedFilePreview = (file) => {
      if (file?.url) {
        uploadedFilePreviewPath.value = file.url
        uploadedFilePreviewTitle.value = file.name || 'Dokumen Surat'
        uploadedFilePreviewVisible.value = true
      }
    }

    // Close preview for uploaded files
    const closeUploadedFilePreview = () => {
      uploadedFilePreviewVisible.value = false
      uploadedFilePreviewPath.value = ''
      uploadedFilePreviewTitle.value = ''
    }

    // Check if file is PDF
    const isPdfFile = (url) => {
      if (!url) return false
      const lowerUrl = url.toLowerCase()
      return lowerUrl.endsWith('.pdf')
    }

    onMounted(() => {
      fetchDetail()
    })

    return {
      pageLoading,
      submitting,
      suratData,
      suratTugasList,
      approveModalVisible,
      reviseModalVisible,
      reviseNotes,
      pdfPreviewVisible,
      pdfPreviewPath,
      pdfPreviewTitle,
      verifikasiStatus,
      isSuratTugas,
      canVerify,
      isUploadFileMethod,
      uploadedFileList,
      uploadedFilePreviewVisible,
      uploadedFilePreviewPath,
      uploadedFilePreviewTitle,
      formatDateTime,
      getStatusColor,
      getStatusText,
      formatVerifikatorList,
      getFileUrl,
      showApproveModal,
      showReviseModal,
      handleCancelRevise,
      handleApprove,
      handleRevise,
      openSuratMasukPreview,
      openUploadedFilePreview,
      closeUploadedFilePreview,
      isPdfFile,
      window,
    }
  },
}
</script>

<style scoped lang="scss">
.verifikasi-detail-page {
  min-height: calc(100vh - 64px);
  padding: 24px;

  @media (max-width: 768px) {
    padding: 16px;
  }

  .page-header {
    margin-bottom: 24px;

    .header-title-wrapper {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 12px;
    }

    h1 {
      margin: 0;
      font-size: 24px;
      font-weight: 600;
      color: #262626;
    }

    .status-surat {
      display: flex;
      gap: 8px;
    }
  }

  .loading-container {
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 400px;
    background: white;
    border-radius: 8px;
    padding: 48px 24px;
  }

  .content-container {
    background: white;
    border-radius: 8px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      padding: 16px;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: #262626;
      margin: 0 0 16px 0;
      padding-bottom: 12px;
      border-bottom: 1px solid #f0f0f0;
    }

    .info-fields {
      margin-bottom: 24px;

      .info-row {
        padding: 10px 0;
        border-bottom: 1px solid #f5f5f5;

        &:last-child {
          border-bottom: none;
        }

        .info-label {
          font-weight: 500;
          color: #595959;
        }

        .info-value {
          color: #262626;

          .file-link {
            color: #1890ff;
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }

          .btn-preview-small,
          .btn-external-small {
            color: #e10f28;
            padding: 2px 6px;
            margin-left: 8px;

            &:hover {
              color: #c41a3d;
              background: transparent;
            }
          }
        }
      }
    }

    .dokumen-section {
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;

      .section-title {
        border-bottom: none;
        padding-bottom: 0;
      }

      .uploaded-files-container {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .file-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 12px;
        background: #f5f5f5;
        border-radius: 4px;
        transition: background 0.2s;

        &:hover {
          background: #e8e8e8;
        }
      }

      .file-icon {
        color: #8c8c8c;
        font-size: 16px;
      }

      .file-link {
        flex: 1;
        color: #262626;
        text-decoration: none;

        &:hover {
          color: #E10F28;
        }
      }

      .file-actions {
        display: flex;
        align-items: center;
        gap: 4px;
      }

      .uploaded-preview-section {
        margin-top: 20px;
        border: 1px solid #e8e8e8;
        border-radius: 8px;
        overflow: hidden;

        .preview-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 16px;
          background: #fafafa;
          border-bottom: 1px solid #e8e8e8;

          .preview-title {
            font-weight: 600;
            color: #262626;
            font-size: 14px;
          }
        }

        .preview-content {
          background: #f5f5f5;
          min-height: 400px;
          display: flex;
          justify-content: center;
          align-items: center;

          .preview-iframe {
            width: 100%;
            height: 500px;
            border: none;
          }

          .preview-image {
            max-width: 100%;
            max-height: 500px;
            object-fit: contain;
          }
        }

        .preview-actions {
          padding: 12px 16px;
          background: #fafafa;
          border-top: 1px solid #e8e8e8;
          display: flex;
          justify-content: flex-end;

          .ant-btn {
            background: #e10f28;
            border-color: #e10f28;
            border-radius: 50px;
            font-weight: 600;

            &:hover {
              background: #c20d23;
              border-color: #c20d23;
            }
          }
        }
      }
    }

    .action-buttons {
      display: flex;
      gap: 16px;
      margin-top: 24px;
      padding-top: 24px;
      border-top: 1px solid #f0f0f0;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 12px;
        margin-top: 16px;
        padding-top: 16px;
      }

      .btn-approve {
        background: #52c41a;
        border-color: #52c41a;
        border-radius: 50px;
        font-weight: 600;
        height: 40px;
        padding: 0 24px;

        @media (max-width: 768px) {
          width: 100%;
        }

        &:hover,
        &:focus {
          background: #73d13d;
          border-color: #73d13d;
        }
      }

      .btn-revise {
        border: 2px solid #ff4d4f;
        color: #ff4d4f;
        border-radius: 50px;
        font-weight: 600;
        height: 40px;
        padding: 0 24px;
        background: white;

        @media (max-width: 768px) {
          width: 100%;
        }

        &:hover {
          border-color: #ff7875;
          color: #ff7875;
        }
      }
    }
  }
}

// Modal Confirm Styles
.modal-confirm {
  padding: 8px 0;

  .modal-title {
    font-size: 18px;
    font-weight: 600;
    color: #1a1a1a;
    margin: 0 0 12px 0;
  }

  .modal-description {
    font-size: 14px;
    color: #595959;
    line-height: 1.6;
    margin: 0 0 24px 0;
  }

  .form-item-revisi {
    margin-bottom: 24px;

    :deep(.ant-form-item-label > label) {
      font-weight: 600;
      color: #262626;
      font-size: 14px;

      &.ant-form-item-required:not(.ant-form-item-required-mark-optional)::before {
        color: #ff4d4f;
      }
    }

    :deep(.ant-input) {
      border-radius: 6px;
    }

    :deep(.ant-input::placeholder) {
      color: #bfbfbf;
    }
  }

  .modal-actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;

    @media (max-width: 768px) {
      flex-direction: column-reverse;
      gap: 8px;
    }

    .btn-cancel {
      border: 2px solid #1a1a1a;
      color: #1a1a1a;
      background: white;
      border-radius: 50px;
      font-weight: 600;
      height: 40px;
      padding: 0 24px;
      font-size: 14px;

      @media (max-width: 768px) {
        width: 100%;
      }
      width: 100%;
      &:hover:not(:disabled) {
        background: #1a1a1a;
        color: white;
      }

      &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }
    }

    .btn-confirm {
      background: #e10f28;
      border: 2px solid #e10f28;
      color: white;
      border-radius: 50px;
      font-weight: 600;
      height: 40px;
      padding: 0 24px;
      font-size: 14px;
      @media (max-width: 768px) {
        width: 100%;
      }
      width: 100%;

      &:hover:not(:disabled) {
        background: #c20d23;
        border-color: #c20d23;
      }

      &:disabled {
        background: #98a2b3;
        border-color: #98a2b3;
        cursor: not-allowed;
      }
    }
  }
}

::v-deep(.ant-tag) {
  font-weight: 700;
  border-radius: 50px;
}

:deep(.ant-form-item-label > label) {
  font-weight: 600;
  color: #262626;
}
</style>
